import React, { useRef, useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
/** @jsxImportSource @emotion/react */
import { css, keyframes } from '@emotion/react'
import {
  Grid,
  Button,
  Form,
  Spinner,
  Layout,
  Input,
  useToaster,
} from '@enterprise-ui/canvas-ui-react'
import '../Custom.css'
import { useFormik } from 'formik'
import { useMutation, useQuery } from '@apollo/client'
import {
  UpdateCaseRequest,
  GET_CASE,
  GET_CASE_ATTACHMENTS,
  CaseAttachments,
  CaseAttachmentVariable,
  CaseResponse,
  UpdateCaseVar,
  UPDATE_CASE,
  AuditEntryInput,
  NoteInput,
  Case,
  CaseVariable,
} from '../Common/Queries'
import { useProfile } from '../../Layout/UserProfile/UserProfileProvider'
import EditDisputeDetails from '../Common/EditDisputeDetails'
import EditVendorDetails from '../Common/EditVendorDetails'
import EditDocumentDetails from '../Common/EditDocumentDetails'

import EditPaymentDetails from '../Common/EditPaymentDetails'
import EditWorkflowDetails from '../Common/EditWorkflowDetails'
import ReopenedCaseBanner from '../Common/ReopenedCaseBanner'
import AttachmentDetails from '../Common/AttachmentDetails'
import DownloadAttachments from '../Common/DownloadAttachments'
import useAttachment from '../Hooks/useAttachment'
import NotesTab from '../Common/NotesTab'
import {
  CaseStatus,
  RTVS_MULTIPLE_REASONS_CODE_TEXT,
} from '../Services/ConstantData'
import ReopenModal from '../Common/ReopenModal'
import {
  EDIT_DISPUTE_HEADING,
  WORKFLOW_SUMMARY_HEADING,
  RESET,
  SET_FIELD,
  ATTACHMENTS,
  COMMENTS,
  REOPEN,
} from '../Common/Constants'
import {
  BottomBorderDiv,
  editButtons,
  editButtonsGroup,
} from '../Common/SynergyDivComponent'
import EditResolutionDetails from '../Common/EditResolutionDetails'
import EditRecieptDetails from '../Common/EditRecieptDetails'
import { useAuth } from '@praxis/component-auth'
import CaseDateSummary from '../Common/CaseDateSummary'
import { useGlobalForm } from '../Context/GlobalFormStateContext'
import BreadCrumb from '../Common/BreadCrumb'
import { activityData } from '../Services/saveActivity'
import SpinnerLoad from '../Common/SpinnerLoad'
import DuplicateCaseSummary from '../Common/DuplicateCaseSummary'
import { findDuplicates } from '../Services/DuplicateCases'
import { useEnv } from '@praxis/component-runtime-env'
import { EnvConfig } from '../../../config/appConfig'
import _ from 'lodash'
import moment from 'moment'
import { getLatestReopenDetail } from '../Common/ReopenUtils'
import useWorkflow from '../Hooks/useWorkflow'
import MultipleReasonCodesTable from '../Common/MultipleReasonCodesTable'

interface Props {
  id: string
  duplicatesCases?: any
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-50%) translateX(4px); }
  to { opacity: 1; transform: translateY(-50%) translateX(0); }
`

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(-50%) translateX(0); }
  to { opacity: 0; transform: translateY(-50%) translateX(4px); }
`

const newFeatureTooltipStyle = (isVisible: boolean) => css`
  position: absolute;
  top: 50%;
  left: 100%;
  transform: translateY(-50%);
  margin-left: 8px;
  background-color: #333;
  color: #fff;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  animation: ${isVisible ? fadeIn : fadeOut} 0.3s ease forwards;
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    right: 100%;
    transform: translateY(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: transparent #333 transparent transparent;
  }
`

const REOPEN_TOOLTIP_SEEN_KEY = 'bp_dispute_reopen_tooltip_seen'

const BPDisputeView: React.FC<Props> = (props) => {
  const { session } = useAuth()
  const env = useEnv() as EnvConfig
  const { userProfile } = useProfile()
  const showActivity = useRef<any>(null)
  const myDivToFocus = useRef<any>(null)
  const [caseUpdate, setCaseUpdate] = useState<string>('')
  const isReopenSubmit = useRef(false)
  const [state, dispatch] = useGlobalForm()
  const [showReopenTooltip, setShowReopenTooltip] = useState(false)
  const userName = session?.userInfo?.fullName ?? ''
  const { data: casedetails, refetch: caseRefetch } = useQuery<
    Case,
    CaseVariable
  >(GET_CASE, {
    variables: {
      id: parseInt(props.id),
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: (disputeDetails: Case) => {
      const caseData = disputeDetails?.getCase
      formik.setFieldValue('claimDetails', caseData?.claimDetails)
      if (caseData?.auditEntries !== null) {
        formik.setFieldValue('auditEntries', caseData?.auditEntries)
      }
      dispatch({
        type: SET_FIELD,
        payload: [
          {
            id: 'savedAttachments',
            value: dataattachments?.getCaseAttachments?.map(function (
              attachmentData: any,
            ) {
              return {
                fileName: attachmentData.fileName,
                documentType: attachmentData.documentType,
                documentNumber: attachmentData.documentNumber,
                attachmentId: attachmentData.attachmentId,
                createdBy: attachmentData.createdBy,
                createdTimestamp: moment(
                  attachmentData?.createdTimestamp,
                ).format('MM/DD/YYYY'),
              }
            }),
          },
          {
            id: 'caseStatus',
            value: caseData?.status,
          },
          { id: 'caseType', value: caseData?.caseType },
          { id: 'caseSubType', value: caseData?.caseSubType },
          {
            id: 'maxRangeVendors',
            value: _.find(
              env.attachments.maxRangeVendors,
              function (maxRangeVendors) {
                return (
                  Number(maxRangeVendors) === Number(caseData?.vendorNumber)
                )
              },
            ),
          },
        ],
      })
    },
    fetchPolicy: 'network-only',
  })
  const duplicateCases = findDuplicates(props.duplicatesCases, props.id)
  const caseData = casedetails?.getCase
  const { isReopenEnabled } = useWorkflow(caseData)
  const { data: dataattachments, refetch: refetchAttachments } = useQuery<
    CaseAttachments,
    CaseAttachmentVariable
  >(GET_CASE_ATTACHMENTS, {
    variables: {
      caseId: props.id,
    },
    fetchPolicy: 'network-only',
  })
  const makeToast = useToaster()

  const [updateDispute, { loading: updateLoading }] = useMutation<
    CaseResponse,
    UpdateCaseVar
  >(UPDATE_CASE, {
    onCompleted: () => {
      isReopenSubmit.current = false
      dispatch({
        type: RESET,
      })
      refetchAttachments()
      makeToast({
        type: 'success',
        heading: caseUpdate,
        message: 'Success',
      })
    },
  })

  useEffect(() => {
    dispatch({
      type: SET_FIELD,
      payload: [
        {
          id: 'savedAttachments',
          value: dataattachments?.getCaseAttachments?.map(function (
            attachmentData: any,
          ) {
            return {
              fileName: attachmentData.fileName,
              documentType: attachmentData.documentType,
              documentNumber: attachmentData.documentNumber,
              attachmentId: attachmentData.attachmentId,
              createdBy: attachmentData.createdBy,
              createdTimestamp: moment(attachmentData?.createdTimestamp).format(
                'MM/DD/YYYY',
              ),
            }
          }),
        },
      ],
    })
  }, [dataattachments, dispatch])

  const formik = useFormik<UpdateCaseRequest>({
    initialValues: {},
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values) => {
      const updateRequest = {
        reasonCode: caseData?.reasonCode,
        reasonCodeText: caseData?.reasonCodeText,
        caseDescription: caseData?.caseDescription,
        status:
          values?.status && values?.status !== CaseStatus.AWAITINGINFO
            ? values?.status
            : caseData?.status === CaseStatus.AWAITINGINFO
              ? CaseStatus.INPROGRESS
              : caseData?.status,
        workflow: caseData?.workflow,
        lanId: caseData?.userProfile?.userId,
        departmentId: caseData?.departmentId,
        purchaseOrderNumber: caseData?.purchaseOrderNumber,
        subContractType: caseData?.subContractType,
        contractId: caseData?.contractId,
        subReasonCode: caseData?.subReasonCode,
        subReasonCodeText: caseData?.subReasonCodeText,
        subReasonCodeText2: caseData?.subReasonCodeText2,
        locationId: caseData?.locationId,
        receiptId: caseData?.receiptId,
        receipts: caseData?.receipts,
        classId: caseData?.classId,
        tcin: caseData?.tcin,
        resolutionCode: Number(caseData?.resolutionCode),
        resolutionCodeText: caseData?.resolutionCodeText,
        resolutionAmount: caseData?.resolutionAmount,
        resolutionText: caseData?.resolutionText,
        resolutionDate: caseData?.resolutionDate,
        documentTypeId: caseData?.documentTypeId,
        documentTypeText: caseData?.documentTypeText,
        documentSubTypeId: caseData?.documentSubTypeId,
        documentSubTypeText: caseData?.documentSubTypeText,
        disputedDocumentAmount: caseData?.disputedDocumentAmount,
        isReopen: caseData?.isReopen,
        disputeDetails: {
          newDocumentNumber: caseData?.disputeDetails?.newDocumentNumber,
          newDocumentAmount: caseData?.disputeDetails?.newDocumentAmount,
          newCheckNumber: caseData?.disputeDetails?.newCheckNumber,
        },
        claimDetails: {
          carrierScacCode: caseData?.claimDetails?.carrierScacCode,
          carrierVendorContact: caseData?.claimDetails?.carrierVendorContact,
          carrierVendorContactEmail:
            caseData?.claimDetails?.carrierVendorContactEmail,
          carrierVendorId: caseData?.claimDetails?.carrierVendorId,
          carrierVendorName: caseData?.claimDetails?.carrierVendorName,
          isMappedVendor: caseData?.claimDetails?.isMappedVendor,
          referenceDocumentCreateDate:
            caseData?.claimDetails?.referenceDocumentCreateDate,
          referenceDocumentNumber:
            caseData?.claimDetails?.referenceDocumentNumber,
          shipDate: caseData?.claimDetails?.shipDate,
          importClaimReversal: {
            approvedPendingReversalAmount:
              values?.claimDetails?.importClaimReversal
                ?.approvedPendingReversalAmount ??
              caseData?.claimDetails?.importClaimReversal
                ?.approvedPendingReversalAmount,
            approvedReversalAmount:
              values?.claimDetails?.importClaimReversal
                ?.approvedReversalAmount ??
              caseData?.claimDetails?.importClaimReversal
                ?.approvedReversalAmount,
            wireRefundAmount:
              values?.claimDetails?.importClaimReversal?.wireRefundAmount ??
              caseData?.claimDetails?.importClaimReversal?.wireRefundAmount,
            writeOffAmount:
              values?.claimDetails?.importClaimReversal?.writeOffAmount ??
              caseData?.claimDetails?.importClaimReversal?.writeOffAmount,
            domesticAccount: {
              domesticVendorId:
                values?.claimDetails?.importClaimReversal?.domesticAccount
                  ?.domesticVendorId ??
                caseData?.claimDetails?.importClaimReversal?.domesticAccount
                  ?.domesticVendorId,
              domesticAccountAmount:
                values?.claimDetails?.importClaimReversal?.domesticAccount
                  ?.domesticAccountAmount ??
                caseData?.claimDetails?.importClaimReversal?.domesticAccount
                  ?.domesticAccountAmount,
            },
            openClaimAmountDetails: values?.claimDetails?.importClaimReversal
              ?.openClaimAmountDetails
              ? values?.claimDetails?.importClaimReversal?.openClaimAmountDetails.map(
                  (claim) => {
                    return {
                      claimDeductionAmount: claim.claimDeductionAmount,
                      claimId: claim.claimId,
                    }
                  },
                )
              : caseData?.claimDetails?.importClaimReversal?.openClaimAmountDetails?.map(
                  (claim) => {
                    return {
                      claimDeductionAmount: claim.claimDeductionAmount,
                      claimId: claim.claimId,
                    }
                  },
                ),
          },
        },
        ...(isReopenSubmit.current && {
          reopenDetails: (() => {
            const latestReopen = getLatestReopenDetail(caseData?.reopenDetails)
            return {
              reopenReasonId: values?.reopenDetails?.reopenReasonId
                ? values?.reopenDetails?.reopenReasonId
                : latestReopen?.reopenReasonId,
              reopenReason: values?.reopenDetails?.reopenReason
                ? values?.reopenDetails?.reopenReason
                : latestReopen?.reopenReason,
              reopenDate: values?.reopenDetails?.reopenDate
                ? values?.reopenDetails?.reopenDate
                : latestReopen?.reopenDate,
              reopenUser: values?.reopenDetails?.reopenUser
                ? values?.reopenDetails?.reopenUser
                : latestReopen?.reopenUser,
              previousResolutionDate: values?.reopenDetails
                ?.previousResolutionDate
                ? values?.reopenDetails?.previousResolutionDate
                : latestReopen?.previousResolutionDate,
              previousResolutionUser: values?.reopenDetails
                ?.previousResolutionUser
                ? values?.reopenDetails?.previousResolutionUser
                : latestReopen?.previousResolutionUser,
              previousResolutionCode: values?.reopenDetails
                ?.previousResolutionCode
                ? values?.reopenDetails?.previousResolutionCode
                : latestReopen?.previousResolutionCode,
              previousResolutionCodeText: values?.reopenDetails
                ?.previousResolutionCodeText
                ? values?.reopenDetails?.previousResolutionCodeText
                : latestReopen?.previousResolutionCodeText,
              previousResolutionText: values?.reopenDetails
                ?.previousResolutionText
                ? values?.reopenDetails?.previousResolutionText
                : latestReopen?.previousResolutionText,
              previousResolutionAmount: values?.reopenDetails
                ?.previousResolutionAmount
                ? values?.reopenDetails?.previousResolutionAmount
                : latestReopen?.previousResolutionAmount,
              previousResolutionDocumentNumber: values?.reopenDetails
                ?.previousResolutionDocumentNumber
                ? values?.reopenDetails?.previousResolutionDocumentNumber
                : latestReopen?.previousResolutionDocumentNumber,
              justificationForReopen: values?.reopenDetails
                ?.justificationForReopen
                ? values?.reopenDetails?.justificationForReopen
                : latestReopen?.justificationForReopen,
            }
          })(),
        }),
        vendorContactName: caseData?.vendorContactName,
        vendorEmailAddress: caseData?.vendorEmailAddress,
        auditEntries:
          values?.auditEntries?.length !== 0
            ? values?.auditEntries
            : caseData?.auditEntries?.map(function (
                auditEntries: AuditEntryInput,
              ) {
                return {
                  activityDescription: auditEntries.activityDescription,
                  userId: auditEntries.userId,
                  workflow: auditEntries.workflow,
                  status: auditEntries.status,
                  assignUserId: auditEntries.assignUserId,
                  activityTimestamp: auditEntries.activityTimestamp,
                }
              }),
        notes:
          values?.notes && values?.notes?.length !== 0
            ? values?.notes
            : caseData?.notes?.map(function (notes: NoteInput) {
                return {
                  userId: notes.userId,
                  userName: notes.userName,
                  commentBody: notes.commentBody,
                  isInternal: notes.isInternal,
                  commentDate: notes.commentDate,
                }
              }),
        agingDate: caseData?.agingDate,
        multipleReasonCodes: caseData?.multipleReasonCodes?.map((rc) => ({
          reasonCode: rc.reasonCode,
          reasonCodeText: rc.reasonCodeText,
          disputedDocumentAmount: rc.disputedDocumentAmount,
        })),
      }
      await updateDispute({
        variables: {
          id: Number(props.id),
          input: updateRequest,
        },
      }).then(() => {
        caseRefetch()
      })
    },
  })
  const { uploadAttachment, attachmentDetails } = useAttachment()
  const prepareToUploadAttachments = async () => {
    setCaseUpdate(ATTACHMENTS)
    formik.setFieldValue('workflow', caseData?.workflow)
    formik.setFieldValue('lanId', caseData?.userProfile?.userId)
    formik.setFieldValue('status', caseData?.status)
    const activityDescription = 'Uploaded Attachment'
    const adAuditEntry = activityData(
      userName,
      activityDescription,
      formik.values,
    )
    formik.setFieldValue('auditEntries', adAuditEntry)
    await uploadAttachment(props.id, caseData?.vendorNumber).then(() =>
      formik.handleSubmit(),
    )
  }

  const onAttachmentFocus = () => {
    if (myDivToFocus.current) {
      myDivToFocus.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
      myDivToFocus.current.focus()
    }
  }
  const onReopenChange = (e: any) => {
    setCaseUpdate(REOPEN)
    isReopenSubmit.current = true
    dispatch({
      type: SET_FIELD,
      payload: [{ id: 'reOpenModalFlag', value: true }],
    })
    formik.setFieldValue('workflow', caseData?.workflow)
    formik.setFieldValue('lanId', caseData?.userProfile?.userId)
  }
  const showActivityCard = () => {
    if (showActivity.current) {
      showActivity.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
      showActivity.current.focus()
    }
  }

  useEffect(() => {
    if (state?.comments) {
      setCaseUpdate(COMMENTS)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.comments])

  // Check sessionStorage for reopen flag on mount
  useEffect(() => {
    const showReopenFromSession = sessionStorage.getItem('showSetReopenButton')
    if (showReopenFromSession === 'true') {
      dispatch({
        type: SET_FIELD,
        payload: [{ id: 'showSetReopenButton', value: true }],
      })
      sessionStorage.removeItem('showSetReopenButton')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Open reopen modal if URL contains reopen=true
  const location = useLocation()
  const reopenTriggeredRef = useRef(false)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (
      params.get('reopen') === 'true' &&
      !reopenTriggeredRef.current &&
      isReopenEnabled &&
      (caseData?.status === CaseStatus.RESOLVED ||
        caseData?.status === CaseStatus.CLOSED) &&
      moment().isBefore(moment(caseData?.createTimestamp).add(18, 'months'))
    ) {
      reopenTriggeredRef.current = true
      onReopenChange(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isReopenEnabled, caseData])

  // Show "New Feature" tooltip on Reopen button for first-time visitors
  useEffect(() => {
    if (
      isReopenEnabled &&
      (caseData?.status === CaseStatus.RESOLVED ||
        caseData?.status === CaseStatus.CLOSED) &&
      !localStorage.getItem(REOPEN_TOOLTIP_SEEN_KEY)
    ) {
      setShowReopenTooltip(true)
      localStorage.setItem(REOPEN_TOOLTIP_SEEN_KEY, 'true')
      const timer = setTimeout(() => {
        setShowReopenTooltip(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReopenEnabled, caseData?.status])

  // Reset reopen button flag when component unmounts
  useEffect(() => {
    return () => {
      dispatch({
        type: SET_FIELD,
        payload: [{ id: 'showSetReopenButton', value: false }],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout.Body includeRail className="hc-pa-md">
      <Grid.Container>
        <BreadCrumb caseId={props.id} />
        <ReopenedCaseBanner caseDetails={caseData} />
        <Grid.Item xs={12}>
          <Button
            type="primary"
            css={editButtons}
            onClick={onAttachmentFocus}
            disabled={caseData?.status === CaseStatus.CLOSED}
          >
            Attach Files
          </Button>
          <Button
            type="primary"
            css={editButtonsGroup}
            onClick={showActivityCard}
          >
            Activity
          </Button>
          {/* {state.showSetReopenButton && */}
          {isReopenEnabled &&
            (caseData?.status === CaseStatus.RESOLVED ||
              caseData?.status === CaseStatus.CLOSED) &&
            moment().isBefore(
              moment(caseData?.createTimestamp).add(18, 'months'),
            ) && (
              <span style={{ position: 'relative', display: 'inline-block' }}>
                <Button
                  type="primary"
                  css={editButtons}
                  onClick={(e: any) => {
                    setShowReopenTooltip(false)
                    onReopenChange(e)
                  }}
                >
                  Reopen
                </Button>
                {showReopenTooltip && (
                  <div css={newFeatureTooltipStyle(showReopenTooltip)}>
                    ✨ Re-open is available for select disputes during early
                    access. For others, continue creating new disputes
                  </div>
                )}
              </span>
            )}
          {updateLoading && <SpinnerLoad case={false} createCase={false} />}
        </Grid.Item>
      </Grid.Container>
      <Grid.Container justify="space-between" className="hc-pa-none">
        <Grid.Item md={7} xs={12}>
          <Form>
            <EditVendorDetails vendorDetails={caseData} />
            <EditDisputeDetails
              heading={EDIT_DISPUTE_HEADING}
              disputeDetails={caseData}
              isVendor={true}
            />
            <MultipleReasonCodesTable
              multipleReasonCodes={
                caseData?.reasonCodeText === RTVS_MULTIPLE_REASONS_CODE_TEXT
                  ? caseData?.multipleReasonCodes
                  : undefined
              }
            />
            <EditDocumentDetails documentDetails={caseData} formik={formik} />
            <EditPaymentDetails paymentDetails={caseData} />
            <EditRecieptDetails receiptDetails={caseData} />
            <div id="myDivToFocus" ref={myDivToFocus}>
              <DownloadAttachments />
              {caseData?.status !== CaseStatus.CLOSED && (
                <BottomBorderDiv>
                  <AttachmentDetails
                    isVendor={userProfile?.isVendor}
                    reasonCode={caseData?.reasonCode}
                    documentNumber={caseData?.originalDocumentNumber}
                    documentSubTypeText={caseData?.documentSubTypeText}
                    availableReasonCodes={caseData?.multipleReasonCodes?.map(
                      (rc: any) => ({
                        id: rc.reasonCode,
                        value: rc.reasonCode,
                        label: rc.reasonCodeText,
                      }),
                    )}
                    multipleReasonCodes={caseData?.multipleReasonCodes}
                  />
                  <div className="hc-pa-normal">
                    <Grid.Container direction="row-reverse">
                      <Grid.Item className="hc-ma-lg">
                        <Button
                          size="dense"
                          type="primary"
                          onClick={prepareToUploadAttachments}
                          disabled={
                            attachmentDetails.uploadingAttachments ||
                            attachmentDetails.attachments?.length === 0 ||
                            attachmentDetails.attachmentTypeNotSet
                          }
                        >
                          {attachmentDetails.uploadingAttachments && (
                            <Spinner size="dense" className="hc-mt-xd" />
                          )}
                          Upload New Attachments
                        </Button>
                        {attachmentDetails.uploadingAttachments && (
                          <Input.Label className="hc-fs-overline hc-mt-xd">
                            Uploading Files ...
                          </Input.Label>
                        )}
                      </Grid.Item>
                    </Grid.Container>
                  </div>
                </BottomBorderDiv>
              )}
            </div>
            {isReopenEnabled && (
              <ReopenModal
                headingText="Reopen the case"
                formik={formik}
                reopenDetails={caseData}
                saveStatus={formik.handleSubmit}
              />
            )}
          </Form>
          <div id="showActivity" ref={showActivity}>
            <Grid.Item className="hc-bg-white">
              <NotesTab
                formik={formik}
                caseDetails={caseData}
                userProfile={userProfile}
                saveComment={formik.handleSubmit}
              />
            </Grid.Item>
          </div>
        </Grid.Item>
        <Grid.Item md={5} xs={12} className="hc-pa-lg">
          <EditWorkflowDetails
            heading={WORKFLOW_SUMMARY_HEADING}
            workflowDetails={caseData}
            attachments={dataattachments?.getCaseAttachments?.length}
          />
          <CaseDateSummary caseDateDetails={caseData} />
          {(caseData?.status === CaseStatus.RESOLVED ||
            caseData?.status === CaseStatus.CLOSED) && (
            <EditResolutionDetails resolutionDetails={caseData} />
          )}

          {duplicateCases && (
            <DuplicateCaseSummary
              heading="Duplicate Cases"
              duplicatesCases={duplicateCases}
            />
          )}
        </Grid.Item>
      </Grid.Container>
    </Layout.Body>
  )
}
export default BPDisputeView
